import _api from '../../stores/_api';
import driverAPI from '@/modules/drivers/_api';

import Store from '../../../models/Store';
import ShipmentArea from '../../../models/ShipmentArea';

export default {
  namespaced: true,
  // -----------------------------------------------------------------
  state: {
    actual: {},
    assignedDrivers: [],
    shipmentAreas: [],
    availableShipmentAreas: [],
    invoices: [],
    orders: [],
    shipmentAreasPageCount: 0,
    shipmentAreasTotalCount: 0,
  },
  // -----------------------------------------------------------------
  getters: {
    actual: (state,) => state.actual,
    actualAssignedDrivers: (state,) => state.assignedDrivers,
    actualShipmentAreas: (state,) => state.shipmentAreas,
    actualAvailableShipmentAreas: (state,) => state.availableShipmentAreas,
    actualShipmentAreasPageCount: (state,) => state.shipmentAreasPageCount,
    actualShipmentAreasTotalCount: (state,) => state.shipmentAreasTotalCount,
    filteredAssignedDrivers: (state,) => (filters,) => state.assignedDrivers.
      filter((item,) => {
        let confirmation = true;
        Object.entries(filters,).forEach((filter,) => {
          if (filter[1]) {
            if (typeof filter[1] === 'string') {
              confirmation = item[filter[0]].
                toLowerCase().indexOf(filter[1].toLowerCase(),) >= 0;
            } else {
              confirmation = item[filter[0]] === filter[1];
            }
          }
        },);
        return confirmation;
      },),
  },
  // -----------------------------------------------------------------
  mutations: {
    newStore: (state,) =>
      state.actual = new Store(),
    setStore: (state, store,) =>
      state.actual.store = store,
    setStoreOrders: (state, orders,) =>
      state.actual.orders = orders,
    setStoreAssignedDrivers: (state, assignedDrivers,) =>
      state.assignedDrivers = assignedDrivers,
    cleanStoreAssignedDrivers: (state,) =>
      state.assignedDrivers = [],
    setStoreShipmentAreas: (state, shipmentAreas,) =>
      state.shipmentAreas = shipmentAreas,
    cleanStoreShipmentAreas: (state,) =>
      state.shipmentAreas = [],
    setStoreShipmentAreasPageCount: (state, shipmentAreasPageCount,) =>
      state.shipmentAreasPageCount = shipmentAreasPageCount,
    cleanStoreShipmentAreasPageCount: (state,) =>
      state.shipmentAreasPageCount = 0,
    setStoreShipmentAreasTotalCount: (state, shipmentAreasTotalCount,) =>
      state.shipmentAreasTotalCount = shipmentAreasTotalCount,
    cleanStoreShipmentAreasTotalCount: (state,) =>
      state.shipmentAreasTotalCount = 0,
    setStoreAvailableShipmentAreas: (state, availableShipmentAreas,) =>
      state.availableShipmentAreas = availableShipmentAreas,
    cleanStoreAvailableShipmentAreas: (state,) =>
      state.availableShipmentAreas = [],
    cleanStore: (state,) => state.actual = {},
  },
  // -----------------------------------------------------------------
  actions: {
    getStoreDetails ({ commit, }, storeID,) {
      commit('cleanStore',);
      commit('newStore',);
      commit('cleanStoreShipmentAreas',);
      commit('cleanStoreShipmentAreasPageCount',);
      commit('cleanStoreShipmentAreasTotalCount',);

      _api.get({ _id: storeID, },).then(
        (response,) => {
          commit('setStore', response.data,);
        },
        (error,) => {
          return _api.handleError(error,);
        },
      );

      driverAPI.getDriverOrders({ _id: storeID, },).then(
        (response,) => {
          commit('setStoreOrders', response.data,);
        },
        () => {
          commit('setStoreOrders', [],);
        },
      );
    },
    getStoreAvailableShipmentAreas ({ commit, }, { storeId, },) {
      commit('cleanStoreAvailableShipmentAreas',);
      _api.getStoreAvailableFences({ _id: storeId, },).then(
        (response,) => {
          commit('setStoreAvailableShipmentAreas', response.data.map((fence,) => new ShipmentArea(fence,),),);
        },
        () => {
          commit('setStoreAvailableShipmentAreas', [],);
        },
      );
    },
    getStoreShipmentAreas ({ commit, }, { storeId, page, limit, district_name, },) {
      commit('cleanStoreShipmentAreas',);
      return _api.getStoreFences({ _id: storeId, limit, page, district_name, },).then(
        (response,) => {
          const shipmentAreas = response.data.items.
            map((fence,) => new ShipmentArea(fence,),);
          commit('setStoreShipmentAreas', shipmentAreas,);
          commit('setStoreShipmentAreasTotalCount', response.data.total_count,);
          commit('setStoreShipmentAreasPageCount', Math.ceil(response.data.total_count / 15,),);
          return shipmentAreas;
        },
        (error,) => {
          return _api.handleError(error,);
        },
        () => {
          commit('setStoreShipmentAreas', [],);
          commit('setStoreShipmentAreasTotalCount', 0,);
          commit('setStoreShipmentAreasPageCount', 0,);
          return {};
        },
      );
    },
    updateStore ({ getters, }, {storeID, valuesToUpdate,},) {
      return _api.patch((getters.actual._id || storeID), valuesToUpdate,);
    },
    updateStoreShipmentAreas ({ getters, }, { fences, storeID, },) {
      return new Promise((resolve, reject,) => {
        _api.putStoreFences({ _id: (getters.actual._id || storeID), body: fences, },).
          then(() => resolve(),).
          catch((error,) => error.response?.status === 400 ?
            reject(error.response.data.errors[0],) : _api.handleError(error,),);
      },);
    },
    removeStoreShipmentArea ({ getters, }, { companyFenceId, storeID, },) {
      return new Promise((resolve, reject,) => {
        _api.removeStoreFence({ _id: (getters.actual._id || storeID), companyFenceId, },).
          then(() => resolve(),).
          catch((error,) => error.response?.status === 400 ?
            reject(error.response.data.errors[0],) : _api.handleError(error,),);
      },);
    },
    updateAssignedDrivers ({ getters, }, { storeID, added_drivers, removed_drivers, },) {
      return _api.
        patchAssignedDrivers(
          { _id: (getters.actual._id || storeID), added_drivers, removed_drivers, },
        );
    },
    addStoreAttachment ({ getters, }, valuesToUpdate,) {
      const attachmentPromises = [];
      valuesToUpdate.forEach((attachment,) => {
        attachmentPromises.push(
          _api.postAttachment(getters.actual._id, attachment,),
        );
      },);
      return Promise.all(attachmentPromises,);
    },
    removeStoreAttachment ({ getters, }, idToRemove,) {
      return _api.removeAttachment(getters.actual._id, idToRemove,);
    },
  },
};
